/**
 * round-selector.ts - Round Selector UI Components
 *
 * Handles building and managing round selector buttons
 * and navigation between rounds.
 */

import { TOURNAMENT_FORMATS } from '../../utils/constants';
import { t } from '../../utils/i18n-helper';

/**
 * Check if we're on the public share page (read-only view)
 */
function isPublicView(): boolean {
  return window.location.pathname.includes('/share/') ||
         document.querySelector('.container.read-only') !== null;
}

/**
 * Parameters for building round selector
 */
interface RoundSelectorParams {
  tournamentId: string;
  tournamentFormat: string;
  roundButtons: string[];
  currentRound: number;
  latestRound: number;
  totalRounds: number;
  allResultsEntered: boolean;
  maxRounds?: number | null;
  playerCount?: number;
}

/**
 * Get knockout round name based on rounds from end
 * @param round - The round number
 * @param totalRounds - Total number of rounds
 * @returns The knockout round name
 */
export function getKnockoutRoundName(round: number, totalRounds: number): string {
  const roundsFromEnd = totalRounds - round + 1;

  if (roundsFromEnd === 1) return t('knockout.final', 'Final');
  if (roundsFromEnd === 2) return t('knockout.semiFinal', 'Semi-Final');
  if (roundsFromEnd === 3) return t('knockout.quarterFinal', 'Quarter-Final');
  if (roundsFromEnd === 4) return t('knockout.roundOf16', 'Round of 16');
  if (roundsFromEnd === 5) return t('knockout.roundOf32', 'Round of 32');
  if (roundsFromEnd === 6) return t('knockout.roundOf64', 'Round of 64');

  // For earlier rounds, just use "Round X"
  return `${t('round.round', 'Round')} ${round}`;
}

/**
 * Build round selector HTML
 * @param params - Parameters for building the selector
 * @returns HTML string for the round selector
 */
export function buildRoundSelector(params: RoundSelectorParams): string {
  const {
    tournamentId,
    tournamentFormat,
    roundButtons,
    currentRound,
    latestRound,
    totalRounds,
    allResultsEntered,
    maxRounds,
    playerCount
  } = params;

  const latestRoundName = tournamentFormat === TOURNAMENT_FORMATS.KNOCKOUT
    ? getKnockoutRoundName(latestRound, totalRounds)
    : `${t('round.round', 'Round')} ${latestRound}`;

  const nextRoundName = tournamentFormat === TOURNAMENT_FORMATS.KNOCKOUT
    ? getKnockoutRoundName(latestRound + 1, totalRounds)
    : `${t('round.round', 'Round')} ${latestRound + 1}`;

  const isFinal = tournamentFormat === TOURNAMENT_FORMATS.KNOCKOUT && latestRoundName === 'Final';
  const hasReachedMaxRounds = tournamentFormat === TOURNAMENT_FORMATS.SWISS && maxRounds && latestRound >= maxRounds;

  // Check if Swiss tournament has enough players for the number of rounds
  // Minimum players = numberOfRounds + 1 (e.g., 3 rounds needs 4 players, 5 rounds needs 6)
  let notEnoughPlayers = false;
  let minimumPlayersNeeded = 0;
  if (tournamentFormat === TOURNAMENT_FORMATS.SWISS && maxRounds && playerCount !== undefined) {
    minimumPlayersNeeded = maxRounds + 1;
    notEnoughPlayers = playerCount < minimumPlayersNeeded;
  }

  // Check if this is a public/share view (no editing allowed)
  const isReadOnly = isPublicView();

  // Generate button (only if results are complete and not at max rounds/final, and not in public view)
  let generateButton = '';
  if (!isReadOnly && allResultsEntered && !hasReachedMaxRounds && !notEnoughPlayers && (tournamentFormat === TOURNAMENT_FORMATS.SWISS || (tournamentFormat === TOURNAMENT_FORMATS.KNOCKOUT && !isFinal))) {
    generateButton = `<button id="generate-pairings-${tournamentId}" class="btn-small btn-generate-round" onclick="generatePairings('${tournamentId}')"><span class="plus-icon">+</span> ${nextRoundName}</button>`;
  }

  // Warning message when not enough players for Swiss tournament
  let notEnoughPlayersWarning = '';
  if (!isReadOnly && tournamentFormat === TOURNAMENT_FORMATS.SWISS && notEnoughPlayers && !hasReachedMaxRounds) {
    notEnoughPlayersWarning = `<div class="not-enough-players-warning" style="margin-top: 8px; padding: 8px 12px; background-color: #fff3cd; border: 1px solid #ffc107; border-radius: 4px; color: #856404; font-size: 0.9em;">
      ⚠️ ${t('pairings.notEnoughPlayers', 'Not enough players for {rounds} rounds. Minimum {min} players required, currently {current}.')
        .replace('{rounds}', String(maxRounds))
        .replace('{min}', String(minimumPlayersNeeded))
        .replace('{current}', String(playerCount))}
      <br><span style="font-size: 0.9em; color: #6c757d;">${t('pairings.notEnoughPlayersHint', 'Either reduce the number of rounds in tournament settings, or add more players.')}</span>
    </div>`;
  }

  // Info message when last round has been reached (show when all results are entered in the final round)
  let lastRoundReachedInfo = '';
  if (!isReadOnly && hasReachedMaxRounds && allResultsEntered) {
    lastRoundReachedInfo = `<div class="last-round-reached-info" style="margin-top: 8px; padding: 8px 12px; background-color: var(--surface-muted, #f8f9fa); border: 1px solid var(--border, #dee2e6); border-radius: 4px; color: var(--text-primary, #212529); font-size: 0.9em;">
      ℹ️ ${t('pairings.lastRoundReached', 'Last round ({round}) has been reached.')
        .replace('{round}', String(maxRounds))}
      <br><span style="font-size: 0.9em; color: var(--text-secondary, #6c757d);">${t('pairings.lastRoundReachedHint', 'Increase the number of rounds in tournament settings to continue.')}</span>
    </div>`;
  }

  // Action buttons
  const actionButtons = [];
  // Delete button - hide in public view
  if (!isReadOnly) {
    // For round-robin, always show delete button since it deletes ALL rounds
    // For other formats, only show when viewing the latest round
    const showDelete = tournamentFormat === TOURNAMENT_FORMATS.ROUND_ROBIN || currentRound === latestRound;
    actionButtons.push(`<button id="delete-round-btn-${tournamentId}" class="btn-icon" onclick="deleteLatestRound('${tournamentId}')" style="display: ${showDelete ? 'inline-block' : 'none'};" title="${t('pairings.deleteRound', 'Delete')} ${latestRoundName}">🗑</button>`);
  }
  // Print button is always shown
  actionButtons.push(`<button id="print-btn-${tournamentId}" class="btn-icon" onclick="printMatches('${tournamentId}')" title="${t('pairings.printMatches', 'Print matches')}">🖨</button>`);

  return `
    <div class="round-selector" data-tournament-format="${tournamentFormat}">
      <div class="round-selector-buttons">
        ${roundButtons.join('')}
        ${generateButton}
      </div>
      <div class="round-selector-actions">
        ${actionButtons.join('')}
      </div>
    </div>
    ${notEnoughPlayersWarning}
    ${lastRoundReachedInfo}
    <div class="results-entry-controls">
      <label class="toggle-switch-container">
        <input type="checkbox" id="hide-completed-${tournamentId}" class="toggle-switch-input" onchange="toggleCompletedResults('${tournamentId}', this.checked)">
        <span class="toggle-switch-slider"></span>
        <span class="toggle-switch-label">${t('pairings.hideCompleted', 'Hide completed results')}</span>
      </label>
      <div class="keyboard-shortcuts-hint">
        ${t('pairings.keyboardShortcuts', 'Keyboard shortcuts')}:
        <strong>1</strong>=1-0,
        <strong>2</strong>=½-½,
        <strong>3</strong>=0-1,
        <strong>4</strong>=1-0ff,
        <strong>5</strong>=0-0,
        <strong>6</strong>=0-1ff
        <span style="margin-left: 8px;">↑↓ ${t('pairings.navigate', 'navigate')}</span>
        <span style="margin-left: 8px;"><strong>b</strong>+# ${t('pairings.goToBoard', 'go to board')}</span>
      </div>
    </div>
  `;
}

/**
 * Show a specific round of matches
 * @param tournamentId - The tournament ID
 * @param round - The round number
 */
export function showMatchRound(tournamentId: string, round: number): void {
  // Hide all round contents
  const allRounds = document.querySelectorAll(`[id^="match-round-${tournamentId}-"]`);
  allRounds.forEach(el => (el as HTMLElement).style.display = 'none');

  // Show selected round
  const selectedRound = document.getElementById(`match-round-${tournamentId}-${round}`);
  if (selectedRound) {
    selectedRound.style.display = 'block';
  }

  // Show/hide delete button based on whether we're viewing the latest round
  const deleteBtn = document.getElementById(`delete-round-btn-${tournamentId}`);
  if (deleteBtn) {
    // Get the tournament format from the round selector data attribute
    const roundSelector = deleteBtn.closest('.round-selector');
    const tournamentFormat = roundSelector?.getAttribute('data-tournament-format');

    // For round-robin, always show delete button since it deletes ALL rounds
    if (tournamentFormat === TOURNAMENT_FORMATS.ROUND_ROBIN) {
      deleteBtn.style.display = 'inline-block';
    } else {
      // For Swiss/Knockout, only show when viewing the latest round
      if (roundSelector) {
        // Find all round buttons (both with onclick and data-round attributes)
        const roundButtonsContainer = roundSelector.querySelector('.round-selector-buttons');
        const allRoundButtons = roundButtonsContainer ? roundButtonsContainer.querySelectorAll('.btn-small') : [];
        let latestRound = 0;
        allRoundButtons.forEach(btn => {
          // Try to get round from data-round attribute first (for single round case)
          let roundNum: number | null = null;
          const dataRound = btn.getAttribute('data-round');
          if (dataRound) {
            roundNum = parseInt(dataRound);
          } else {
            // Otherwise parse from onclick attribute (for multi-round case)
            const onclick = btn.getAttribute('onclick');
            const onclickMatch = onclick?.match(/showMatchRound\('[^']+',\s*(\d+)\)/);
            if (onclickMatch && onclickMatch[1]) {
              roundNum = parseInt(onclickMatch[1]);
            }
          }
          if (roundNum && roundNum > latestRound) {
            latestRound = roundNum;
          }
        });
        deleteBtn.style.display = round === latestRound ? 'inline-block' : 'none';
      }
    }
  }

  // Update button states for THIS tournament only
  // Find the round selector that contains a button for this tournament
  const roundSelectors = document.querySelectorAll('.round-selector');
  roundSelectors.forEach(selector => {
    // Check if this selector has a button for this tournament
    const tournamentButton = selector.querySelector(`[onclick*="'${tournamentId}'"]`);
    if (tournamentButton) {
      // This is the right selector for this tournament
      const allButtons = selector.querySelectorAll('.btn-small[onclick^="showMatchRound"]');
      allButtons.forEach(btn => {
        // Match by onclick handler to work for all tournament types (swiss, round-robin, knockout)
        const onclick = btn.getAttribute('onclick');
        const onclickMatch = onclick?.match(/showMatchRound\('([^']+)',\s*(\d+)\)/);
        if (onclickMatch) {
          const [, btnTournamentId, btnRoundStr] = onclickMatch;
          const btnRound = btnRoundStr ? parseInt(btnRoundStr) : 0;
          if (btnTournamentId === tournamentId && btnRound === round) {
            btn.classList.remove('btn-secondary');
            btn.classList.add('btn-primary');
          } else {
            btn.classList.remove('btn-primary');
            btn.classList.add('btn-secondary');
          }
        }
      });
    }
  });
}

// Export to window for onclick handlers

window.buildRoundSelector = buildRoundSelector;
window.showMatchRound = showMatchRound;
window.getKnockoutRoundName = getKnockoutRoundName;
